"use client";

import { useEffect } from "react";
import { AlertTriangle } from "lucide-react";

import { ApiError } from "@/api/core/ApiError";

type GlobalErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

/**
 * Global error boundary for root-level errors
 * This catches errors that escape the app error boundary (e.g., errors in layout.tsx)
 */
export default function GlobalError(
  props: Readonly<GlobalErrorProps>,
): JSX.Element {
  useEffect(() => {
    // Log the error to an error reporting service
    const errorStatus = (props.error as ApiError & { status?: number })?.status;
    const isApiError = props.error instanceof ApiError || errorStatus !== undefined;

    console.error("Global error:", {
      name: props.error.name,
      message: props.error.message,
      stack: props.error.stack,
      digest: props.error.digest,
      ...(isApiError && {
        status: errorStatus,
        url: (props.error as ApiError).url,
      }),
    });
  }, [props.error]);

  return (
    <html lang="en">
      <body>
        <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
          <div className="w-full max-w-md text-center">
            <div className="mb-6 flex justify-center">
              <div className="flex size-20 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                <AlertTriangle className="size-10 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <h1 className="mb-4 text-4xl font-bold text-gray-900 dark:text-white">
              Something went wrong
            </h1>
            <p className="mb-6 text-gray-600 dark:text-gray-400">
              A critical error occurred. Please refresh the page or contact support if the problem persists.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => props.reset()}
                className="w-full rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
              >
                Try again
              </button>
              <button
                onClick={() => {
                  window.location.href = "/";
                }}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Go to homepage
              </button>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
